import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import useLang from "./../../hooks/useLanguage";

// Committed in local time, same as the `Date` uiType (which sends MM/DD/YYYY)
// — just with the time part appended. No UTC conversion.
const DISPLAY_FORMAT = "MM/DD/YYYY hh:mm A";

// Read side is tolerant: a date-only value, a 24h time, or an ISO timestamp
// coming back from the API all render correctly.
const ACCEPTED_FORMATS: any[] = [
  "MM/DD/YYYY hh:mm A",
  "MM/DD/YYYY HH:mm",
  "MM/DD/YYYY hh:mm:ss A",
  "MM/DD/YYYY",
  moment.ISO_8601,
];

// Fields that can never be in the future.
const PAST_ONLY_FIELDS = [
  "DateofCollection",
  "DateReceived",
  "DOB",
  "SubscriberDOB",
  "AccidentDate",
  "ConsentDate",
  "MostRecentSurgeryDate",
  "ImagingDate",
];

// Mirrors the theme's `.form-control` (src/_theme/assets/css/style.bundle.css)
// so this picker sits flush with every other input on the form.
const FORM_CONTROL = {
  fontFamily: "var(--bs-body-font-family)",
  fontSize: "1rem",
  lineHeight: 1.5,
  color: "#3F4254",
  background: "#ffffff",
  borderHover: "var(--kt-gray-500)",
  borderWidth: "2px",
  borderRadius: "0.85rem",
  height: "calc(1.5em + 1.3rem + 2px)",
  padding: "0.65rem 1rem",
  placeholder: "var(--kt-input-solid-placeholder-color)",
  icon: "var(--kt-gray-600)",
  danger: "var(--kt-danger)",
  activeBg: "var(--kt-component-active-bg)",
  activeColor: "var(--kt-component-active-color)",
  hoverBg: "var(--kt-component-hover-bg)",
};

// Committed value -> the moment the picker displays.
const parseValue = (value: any): Moment | null => {
  if (!value) return null;

  if (value instanceof Date) {
    const fromDate = moment(value);
    return fromDate.isValid() ? fromDate : null;
  }

  const raw = `${value}`.trim();
  if (!raw) return null;

  const parsed = moment(raw, ACCEPTED_FORMATS);
  return parsed.isValid() ? parsed : null;
};

const DateTimeInput = (props: any) => {
  const { t } = useLang();
  const divElement = useRef<HTMLDivElement | null>(null);

  const [value, setValue] = useState<Moment | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  // The last value this component pushed up, so the sync effect can tell an
  // external change apart from the echo of our own commit. Without it a
  // half-typed value (committed as "") would immediately wipe what the user
  // is still typing.
  const lastEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    const incoming = `${props.value ?? ""}`;
    if (
      lastEmittedRef.current !== null &&
      incoming === lastEmittedRef.current
    ) {
      return;
    }

    lastEmittedRef.current = null;
    setValue(parseValue(props.value));
  }, [props.value]);

  // Only a complete, valid date + time is committed. Anything else is committed
  // as empty so required-validation keeps blocking the save.
  const emitChange = (selected: Moment | null) => {
    const nextValue =
      selected && selected.isValid() ? selected.format(DISPLAY_FORMAT) : "";

    lastEmittedRef.current = nextValue;
    props.onChange?.({
      target: { name: props.name, value: nextValue },
    });
  };

  const handleChange = (selected: Moment | null) => {
    setValue(selected);
    setIsInvalid(false);
    emitChange(selected);
  };

  const handleError = (error: any) => {
    setIsInvalid(!!error);
    // An out-of-range / incomplete entry must not stay committed.
    if (error) emitChange(null);
  };

  const getMaxDateTime = () => {
    if (
      (props.name === "DateofCollection" || props.name === "DateReceived") &&
      typeof props.fieldConfiguration === "number" &&
      props.fieldConfiguration > 0 &&
      Number.isInteger(props.fieldConfiguration)
    ) {
      return moment().add(props.fieldConfiguration, "days").endOf("day");
    }

    return PAST_ONLY_FIELDS.includes(props.name) ? moment() : undefined;
  };

  useEffect(() => {
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    props.setErrorFocussedInput?.();
  }, [props?.errorFocussedInput, props.error]);

  const hasError = isInvalid || !!props.error;
  const borderHoverColor = hasError
    ? FORM_CONTROL.danger
    : FORM_CONTROL.borderHover;

  return (
    <div
      className={
        props?.parentDivClassName
          ? `${props.parentDivClassName} mb-4 d-flex flex-column`
          : "col-lg-6 col-md-6 col-sm-12 mb-4"
      }
      ref={divElement}
    >
      <label
        className={props?.required ? "required mb-2 fw-500" : "mb-2 fw-500"}
        htmlFor={props?.name}
      >
        {t(props.label)}
      </label>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DateTimePicker
          value={value}
          onChange={handleChange}
          onError={handleError}
          format={DISPLAY_FORMAT}
          maxDateTime={getMaxDateTime()}
          slotProps={{
            field: { clearable: true },
            textField: {
              fullWidth: true,
              required: props?.required,
              id: props?.name,
              name: props?.name,
              error: hasError,
              placeholder: "mm/dd/yyyy hh:mm aa",
              sx: {
                "& .MuiOutlinedInput-root": {
                  height: FORM_CONTROL.height,
                  fontFamily: FORM_CONTROL.fontFamily,
                  fontSize: FORM_CONTROL.fontSize,
                  lineHeight: FORM_CONTROL.lineHeight,
                  color: FORM_CONTROL.color,
                  backgroundColor: FORM_CONTROL.background,
                  borderRadius: FORM_CONTROL.borderRadius,
                  transition: "border-color .15s ease-in-out",
                  "& fieldset": {
                    borderWidth: FORM_CONTROL.borderWidth,
                  },
                  // Same width in every state so focus causes no layout jump.
                  "&:hover fieldset, &.Mui-focused fieldset": {
                    borderColor: borderHoverColor,
                    borderWidth: FORM_CONTROL.borderWidth,
                  },
                  "&.Mui-error fieldset": {
                    borderColor: FORM_CONTROL.danger,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: FORM_CONTROL.padding,
                  height: "1.5em",
                  lineHeight: FORM_CONTROL.lineHeight,
                  fontFamily: FORM_CONTROL.fontFamily,
                  fontSize: FORM_CONTROL.fontSize,
                  color: FORM_CONTROL.color,
                  "&::placeholder": {
                    color: FORM_CONTROL.placeholder,
                    opacity: 1,
                  },
                },
                "& .MuiInputAdornment-root": {
                  marginLeft: 0,
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.1rem",
                  color: FORM_CONTROL.icon,
                },
                "& .MuiIconButton-root": {
                  padding: "6px",
                  "&:hover": {
                    backgroundColor: FORM_CONTROL.hoverBg,
                  },
                },
              },
            },
            popper: {
              sx: {
                fontFamily: FORM_CONTROL.fontFamily,
                "& .MuiPaper-root": {
                  fontFamily: FORM_CONTROL.fontFamily,
                  borderRadius: FORM_CONTROL.borderRadius,
                  border: "1px solid var(--kt-border-color)",
                  boxShadow: "var(--kt-dropdown-box-shadow)",
                  marginTop: "4px",
                },
                "& .MuiTypography-root, & .MuiButtonBase-root": {
                  fontFamily: FORM_CONTROL.fontFamily,
                },
                "& .MuiPickersCalendarHeader-label": {
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "var(--kt-text-gray-900)",
                },
                "& .MuiPickersArrowSwitcher-root .MuiSvgIcon-root": {
                  color: FORM_CONTROL.icon,
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "var(--kt-text-gray-600)",
                },
                "& .MuiPickersDay-root": {
                  fontSize: "0.9rem",
                  borderRadius: "0.475rem",
                  color: "var(--kt-text-gray-800)",
                  "&:hover": {
                    backgroundColor: FORM_CONTROL.hoverBg,
                    color: "var(--kt-component-hover-color)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: `${FORM_CONTROL.activeBg} !important`,
                    color: `${FORM_CONTROL.activeColor} !important`,
                    fontWeight: 500,
                  },
                  "&.MuiPickersDay-today:not(.Mui-selected)": {
                    border: `1px solid ${FORM_CONTROL.activeBg}`,
                  },
                },
                "& .MuiPickersDay-dayOutsideMonth": {
                  color: "var(--kt-text-muted)",
                },
                // Digital clock columns (hours / minutes / am-pm)
                "& .MuiMultiSectionDigitalClockSection-item": {
                  fontSize: "0.9rem",
                  borderRadius: "0.475rem",
                  color: "var(--kt-text-gray-800)",
                  "&:hover": {
                    backgroundColor: FORM_CONTROL.hoverBg,
                  },
                  "&.Mui-selected": {
                    backgroundColor: `${FORM_CONTROL.activeBg} !important`,
                    color: `${FORM_CONTROL.activeColor} !important`,
                    fontWeight: 500,
                  },
                },
                "& .MuiDialogActions-root .MuiButton-root": {
                  fontFamily: FORM_CONTROL.fontFamily,
                  textTransform: "none",
                  color: FORM_CONTROL.activeBg,
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      {/* Same error markup the other inputs on the form use. */}
      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}
    </div>
  );
};

export default DateTimeInput;
